/**
 * Records a browser voice call (caller mic + AI audio mixed) with
 * MediaRecorder, for upload to /portal/:slug/voice/session/:id/recording
 * where it is encrypted like any call recording. Best effort: a browser
 * without MediaRecorder / Opus simply produces no recording.
 */
export interface CallRecorder {
  /** Node to connect extra audio into (ElevenLabs playback sources). */
  input: AudioNode
  stop: () => Promise<Blob | null>
}

export function startCallRecorder(ctx: AudioContext, streams: MediaStream[]): CallRecorder | null {
  if (typeof MediaRecorder === 'undefined') return null
  const dest = ctx.createMediaStreamDestination()
  for (const s of streams) {
    if (s.getAudioTracks().length) ctx.createMediaStreamSource(s).connect(dest)
  }
  const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find((m) => MediaRecorder.isTypeSupported(m))
  let rec: MediaRecorder
  try {
    rec = mime ? new MediaRecorder(dest.stream, { mimeType: mime }) : new MediaRecorder(dest.stream)
  } catch {
    return null
  }
  const chunks: Blob[] = []
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data) }
  rec.start(5000)
  return {
    input: dest,
    stop: () => new Promise((resolve) => {
      if (rec.state === 'inactive') { resolve(chunks.length ? new Blob(chunks, { type: rec.mimeType }) : null); return }
      rec.onstop = () => resolve(chunks.length ? new Blob(chunks, { type: rec.mimeType }) : null)
      try { rec.stop() } catch { resolve(null) }
    }),
  }
}

export async function uploadRecording(tenantSlug: string, sessionId: string, token: string, blob: Blob): Promise<void> {
  if (blob.size < 1000) return
  await fetch(`/api/portal/${tenantSlug}/voice/session/${sessionId}/recording`, {
    method: 'POST',
    headers: { 'content-type': blob.type.split(';')[0] || 'audio/webm', 'x-voice-token': token },
    body: blob,
    credentials: 'include',
  }).catch(() => undefined)
}
